import type { SearchTierConfig } from "@gencow/core";
import { sql, type SQL } from "drizzle-orm";

export type LexicalStrategy = "pgroonga" | "tsvector_strict" | "tsvector_relaxed";

export type LexicalPlan = {
  normalizedQuery: string;
  strategy: LexicalStrategy;
  terms: [string, ...string[]];
  textSearchConfig: "english" | "simple";
};

type SafeIdentifier = (identifier: string) => string;

const RELAXED_STOP_TERMS = new Set([
  "about",
  "info",
  "information",
  "me",
  "please",
  "summary",
  "summarize",
  "tell",
  "관련",
  "관련된",
  "대해",
  "대한",
  "설명",
  "설명해줘",
  "설명해주세요",
  "알려줘",
  "알려주세요",
  "요약",
  "요약해줘",
  "요약해주세요",
  "정보",
  "정리",
  "정리해줘",
  "정리해주세요",
  "좀",
  "해줘",
  "해주세요",
]);

export function normalizeSearchQuery(query: string): string {
  return query.normalize("NFC").trim().replace(/\s+/g, " ");
}

export function extractRelaxedLexicalTerms(query: string): string[] {
  const matches = query.match(/[\p{L}\p{N}_-]+/gu) ?? [];
  const seen = new Set<string>();
  const terms: string[] = [];

  for (const rawTerm of matches) {
    const term = rawTerm.normalize("NFC").trim();
    const termKey = term.toLocaleLowerCase();
    if (!term || RELAXED_STOP_TERMS.has(termKey) || seen.has(termKey)) {
      continue;
    }
    seen.add(termKey);
    terms.push(term);
  }

  return terms;
}

export function createLexicalPlan(
  config: Pick<SearchTierConfig, "engine" | "locale">,
  query: string,
): LexicalPlan {
  const normalizedQuery = normalizeSearchQuery(query);
  const strategy =
    config.engine === "pgroonga"
      ? "pgroonga"
      : config.locale === "multilingual"
        ? "tsvector_relaxed"
        : "tsvector_strict";
  const shouldExtractTerms = config.locale === "multilingual" && strategy !== "tsvector_strict";
  const extractedTerms = shouldExtractTerms ? extractRelaxedLexicalTerms(normalizedQuery) : [];
  const terms = (extractedTerms.length > 0 ? extractedTerms : [normalizedQuery]) as [string, ...string[]];

  return {
    normalizedQuery,
    strategy,
    terms,
    textSearchConfig: strategy === "tsvector_relaxed" ? "simple" : "english",
  };
}

function joinSql(chunks: SQL[], separator: string): SQL {
  if (chunks.length === 0) {
    return sql`true`;
  }

  let current = chunks[0];
  for (const chunk of chunks.slice(1)) {
    current = sql`${current}${sql.raw(separator)}${chunk}`;
  }
  return current;
}

function buildFieldConcat(fields: string[], safeIdentifier: SafeIdentifier): SQL {
  const chunks = fields.map((field) => sql.raw(`coalesce(${safeIdentifier(field)}::text, '')`));
  return joinSql(chunks, ` || ' ' || `);
}

function buildTextSearchConfig(config: LexicalPlan["textSearchConfig"]): SQL {
  return sql.raw(config === "simple" ? "'simple'" : "'english'");
}

function buildTextQuery(plan: LexicalPlan): SQL {
  const textSearchConfig = buildTextSearchConfig(plan.textSearchConfig);
  if (plan.strategy === "tsvector_relaxed" && plan.terms.length > 1) {
    return sql`(${joinSql(
      plan.terms.map((term) => sql`plainto_tsquery(${textSearchConfig}, ${term})`),
      " || ",
    )})`;
  }

  return sql`plainto_tsquery(${textSearchConfig}, ${plan.terms[0]})`;
}

export function buildLexicalClause(plan: LexicalPlan, fields: string[], safeIdentifier: SafeIdentifier): SQL {
  if (plan.strategy === "pgroonga") {
    const queries =
      plan.terms[0] === plan.normalizedQuery ? [plan.normalizedQuery] : [plan.normalizedQuery, ...plan.terms];
    const fieldClauses = fields.flatMap((field) =>
      queries.map((query) => sql`${sql.raw(safeIdentifier(field))} &@~ ${query}`),
    );
    return sql`(${joinSql(fieldClauses, " OR ")})`;
  }

  const fieldConcat = buildFieldConcat(fields, safeIdentifier);
  const textSearchConfig = buildTextSearchConfig(plan.textSearchConfig);
  return sql`to_tsvector(${textSearchConfig}, ${fieldConcat}) @@ ${buildTextQuery(plan)}`;
}

export function buildLexicalScore(plan: LexicalPlan, fields: string[], safeIdentifier: SafeIdentifier): SQL {
  if (plan.strategy === "pgroonga") {
    return sql`pgroonga_score(tableoid, ctid)::double precision`;
  }

  const fieldConcat = buildFieldConcat(fields, safeIdentifier);
  const textSearchConfig = buildTextSearchConfig(plan.textSearchConfig);
  return sql`ts_rank(to_tsvector(${textSearchConfig}, ${fieldConcat}), ${buildTextQuery(plan)})::double precision`;
}
